import { useEffect, useRef, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { revealPayoutCounterparty } from '../../services/LedgerService';
import { formatPhoneForDisplay, formatReceiverPartyForDisplay } from '../../utils/phoneFormat';
import { shareReceiptText } from '../../utils/shareReceipt';

// The modal auto-closes after this many milliseconds so a phone left face
// up on a table does not keep the raw counterparty on screen. A minute
// leaves enough time for wait staff or a driver to read the receipt code
// aloud and copy it onto a paper receipt without the modal vanishing mid
// sentence.
const AUTO_CLOSE_MS = 60_000;

const recipientLabelClass = 'badge bg-light text-primary text-uppercase fw-semibold';

const statusDescriptor = (status) => {
    switch (status) {
        case 'successful':   return { cls: 'text-success',   bg: 'bg-success-subtle',   icon: 'fa-check-circle',        label: 'Successful' };
        case 'under_review': return { cls: 'text-info',      bg: 'bg-info-subtle',      icon: 'fa-search',              label: 'Under Review' };
        case 'pending':      return { cls: 'text-warning',   bg: 'bg-warning-subtle',   icon: 'fa-hourglass-half',      label: 'Pending' };
        case 'failed':       return { cls: 'text-danger',    bg: 'bg-danger-subtle',    icon: 'fa-times-circle',        label: 'Failed' };
        default:             return { cls: 'text-secondary', bg: 'bg-secondary-subtle', icon: 'fa-question-circle',     label: 'Unknown' };
    }
};

const formatAmount = (amount) =>
    amount != null
        ? `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
        : null;

const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const CounterpartyReveal = ({
    payoutId,
    maskedValue,
    payout,
    requiresFetch = true,
    isOpen: isOpenProp,
    onOpenChange,
    hideMasked = false,
    hideEye = false,
}) => {
    const isControlled = typeof isOpenProp === 'boolean';
    const [isOpenInternal, setIsOpenInternal] = useState(false);
    const isOpen = isControlled ? isOpenProp : isOpenInternal;
    const [rawReceiverParty, setRawReceiverParty] = useState(null);
    const [isFetching, setIsFetching] = useState(false);
    const autoCloseTimer = useRef(null);
    const lastLoadedFor = useRef(null);
    const activeRequestId = useRef(0);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
        };
    }, []);

    const setOpen = (next) => {
        if (!isControlled) setIsOpenInternal(next);
        if (onOpenChange) onOpenChange(next);
    };

    const loadRecipient = async () => {
        const requestId = ++activeRequestId.current;
        if (!requiresFetch) {
            if (isMountedRef.current && activeRequestId.current === requestId) {
                setRawReceiverParty(maskedValue);
            }
            return;
        }
        setIsFetching(true);
        try {
            const response = await revealPayoutCounterparty(payoutId);
            if (!isMountedRef.current || activeRequestId.current !== requestId) return;
            setRawReceiverParty(response?.data?.data?.receiver_party ?? maskedValue);
        } catch (error) {
            if (!isMountedRef.current || activeRequestId.current !== requestId) return;
            toast.error(error?.response?.data?.message || 'Could not show the recipient details. Please try again.');
            setRawReceiverParty(maskedValue);
        } finally {
            if (isMountedRef.current && activeRequestId.current === requestId) {
                setIsFetching(false);
            }
        }
    };

    useEffect(() => {
        if (!isOpen) {
            lastLoadedFor.current = null;
            return;
        }
        if (lastLoadedFor.current === payoutId) return;
        lastLoadedFor.current = payoutId;
        loadRecipient();
        if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
        autoCloseTimer.current = setTimeout(() => handleClose(), AUTO_CLOSE_MS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, payoutId]);

    const handleOpen = (event) => {
        if (event) event.stopPropagation();
        setOpen(true);
    };

    const handleClose = () => {
        if (autoCloseTimer.current) {
            clearTimeout(autoCloseTimer.current);
            autoCloseTimer.current = null;
        }
        setOpen(false);
        setRawReceiverParty(null);
    };

    const parsedName = (() => {
        if (!rawReceiverParty || !rawReceiverParty.includes(' - ')) return null;
        return rawReceiverParty.split(' - ').slice(1).join(' - ').trim();
    })();
    const parsedPhone = (() => {
        if (!rawReceiverParty) return null;
        if (!rawReceiverParty.includes(' - ')) return rawReceiverParty;
        return rawReceiverParty.split(' - ')[0].trim();
    })();
    const formattedPhone = formatPhoneForDisplay(parsedPhone);

    return (
        <>
            {(!hideMasked || !hideEye) && (
            <span className="d-inline-flex align-items-center gap-2">
                {!hideMasked && <span>{formatReceiverPartyForDisplay(maskedValue)}</span>}
                {!hideEye && (
                    <button
                        type="button"
                        className="counterparty-reveal-eye btn btn-link p-0 text-primary d-inline-flex align-items-center gap-1 text-decoration-none"
                        onClick={handleOpen}
                        aria-label="Show recipient details"
                        title="Show recipient details"
                        style={{ fontSize: '0.85rem', lineHeight: 1 }}
                    >
                        <i className="fa fa-eye" aria-hidden="true" />
                        <span className="fw-medium">View</span>
                    </button>
                )}
            </span>
            )}
            {/* React synthetic events bubble from a portaled Modal through
                the React component tree back to the clickable row, so this
                shield stops a close click from re-opening the modal. */}
            <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
            <Modal
                show={isOpen}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6 text-muted">Recipient details</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    {payout?.status && (() => {
                        const status = statusDescriptor(payout.status);
                        return (
                            <div className="d-flex flex-column align-items-center mb-3" style={{ gap: '2px' }}>
                                <span
                                    className={`d-inline-flex align-items-center justify-content-center rounded-circle ${status.bg} ${status.cls}`}
                                    style={{ width: '72px', height: '72px', fontSize: '2rem' }}
                                >
                                    <i className={`fa ${status.icon}`} aria-hidden="true" />
                                </span>
                                <span className={`fs-5 fw-semibold ${status.cls}`}>{status.label}</span>
                            </div>
                        );
                    })()}
                    {isFetching ? (
                        <div className="py-4">
                            <Spinner animation="border" role="status" />
                        </div>
                    ) : (
                        <>
                            <div className="mb-3">
                                {payout?.payout_type === 'paybill' ? (
                                    <span className={recipientLabelClass}>Paybill Payment</span>
                                ) : payout?.payout_type === 'till' ? (
                                    <span className={recipientLabelClass}>Till Payment</span>
                                ) : payout?.payout_type === 'pochi-la-biashara' ? (
                                    <span className={recipientLabelClass}>Pochi Payment</span>
                                ) : payout?.payout_type === 'send-money' ? (
                                    <span className={recipientLabelClass}>Send Money</span>
                                ) : (
                                    <span className={recipientLabelClass}>Payment</span>
                                )}
                            </div>
                            {parsedName && (
                                <div className="fs-2 fw-bold text-body mb-3" style={{ wordBreak: 'break-word' }}>
                                    {parsedName}
                                </div>
                            )}
                            {formattedPhone && (
                                <div className="fs-4 mb-3 d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                    <span className="text-body">{formattedPhone}</span>
                                </div>
                            )}
                            {payout?.account_reference && (
                                <div className="fs-6 mb-3 d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                    <span className={recipientLabelClass}>Account</span>
                                    <span className="text-body">{payout.account_reference}</span>
                                </div>
                            )}
                            {formatAmount(payout?.amount) && (
                                <div className="fs-5 fw-semibold text-primary mb-3">
                                    {formatAmount(payout.amount)}
                                </div>
                            )}
                            {payout?.post_mpesa_receipt_number && (
                                <div className="mb-3">
                                    <div className="small text-muted mb-1">Receipt</div>
                                    <div
                                        className="fs-2 fw-bold text-primary"
                                        style={{ fontFamily: 'Menlo, Consolas, "Liberation Mono", monospace', letterSpacing: '0.22em', wordBreak: 'break-word' }}
                                    >
                                        {payout.post_mpesa_receipt_number}
                                    </div>
                                </div>
                            )}
                            {formatDate(payout?.post_mpesa_transaction_time ?? payout?.created_at) && (
                                <div className="mt-3 pt-3 border-top">
                                    <div className="small text-uppercase text-muted mb-1" style={{ letterSpacing: '0.06em' }}>Sent at</div>
                                    <div className="text-body">
                                        {formatDate(payout.post_mpesa_transaction_time ?? payout.created_at)}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-center border-0 pt-0 flex-column gap-2">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm px-4 rounded-pill"
                        onClick={() => {
                            const typeLabel = payout?.payout_type === 'paybill' ? 'Paybill Payment'
                                : payout?.payout_type === 'till' ? 'Till Payment'
                                : payout?.payout_type === 'pochi-la-biashara' ? 'Pochi Payment'
                                : payout?.payout_type === 'send-money' ? 'Send Money'
                                : 'Payment';
                            const lines = [
                                'Milipot receipt',
                                '',
                                typeLabel,
                            ];
                            if (parsedName) lines.push(parsedName);
                            if (formattedPhone) lines.push(formattedPhone);
                            if (payout?.account_reference) lines.push(`Account: ${payout.account_reference}`);
                            if (formatAmount(payout?.amount)) lines.push(formatAmount(payout.amount));
                            if (payout?.post_mpesa_receipt_number) lines.push(`Receipt: ${payout.post_mpesa_receipt_number}`);
                            const date = formatDate(payout?.post_mpesa_transaction_time ?? payout?.created_at);
                            if (date) lines.push(`Sent at ${date}`);
                            shareReceiptText(lines.join('\n'), 'Milipot payment receipt');
                        }}
                    >
                        <i className="bi bi-share-fill me-2"></i>
                        Share receipt
                    </button>
                    <small className="text-muted fst-italic">Hides automatically after 60 seconds.</small>
                </Modal.Footer>
            </Modal>
            </span>
        </>
    );
};

export default CounterpartyReveal;
